"use client";

import { useEffect, useRef, useState } from "react";
import { type RealtimeStatus, realtimeClient } from "@/lib/realtime/client";
import type { RealtimeEvent } from "@/lib/realtime/events";

/**
 * Subscribe to a realtime channel for the lifetime of the component. Pass a
 * falsy `channel` (or `enabled=false`) to stay unsubscribed — handy when the
 * id isn't known yet. The latest `onEvent` is always used, so an inline closure
 * is fine without memoizing.
 *
 * Build channel strings with the helpers in `@/lib/realtime/events`
 * (e.g. `chatChannel(id)`).
 */
export function useRealtimeChannel(
  channel: string | null | undefined,
  onEvent: (event: RealtimeEvent) => void,
  enabled = true,
): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!channel || !enabled) return;
    return realtimeClient.subscribe(channel, (event) =>
      handlerRef.current(event),
    );
  }, [channel, enabled]);
}

export function useRealtimeChannels(
  channels: readonly string[],
  onEvent: (event: RealtimeEvent) => void,
  enabled = true,
): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const channelKey = [...new Set(channels.filter(Boolean))].join("\0");

  useEffect(() => {
    if (!enabled) return;
    const uniqueChannels = channelKey ? channelKey.split("\0") : [];
    const unsubscribe = uniqueChannels.map((channel) =>
      realtimeClient.subscribe(channel, (event) => handlerRef.current(event)),
    );
    return () => {
      for (const unsub of unsubscribe) unsub();
    };
  }, [channelKey, enabled]);
}

/**
 * Subscribe to the signed-in user's own `user:<id>` channel — personal events
 * like new notifications, unread-badge changes, chat-list touches, and payment
 * status. The server auto-joins this channel on connect, so no id is needed.
 */
export function useUserRealtime(onEvent: (event: RealtimeEvent) => void): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(
    () => realtimeClient.subscribeUser((event) => handlerRef.current(event)),
    [],
  );
}

/**
 * Run `callback` whenever the socket establishes, including its first connection (network blip
 * or background→foreground resume). Use it to trigger a delta resync so the UI
 * catches up on anything missed while disconnected.
 *
 * Keep reconciliation narrow. Reconnects are not
 * rare events: the gateway runs on ephemeral instances, so this fires on a
 * cadence set by infrastructure rather than by the user. A `router.refresh()`
 * wired to it would re-fetch the current route's dynamic data on that same
 * cadence — a refetch storm that competes with the navigation the user is
 * waiting on. Resync narrowly (invalidate the specific queries that could have
 * missed an event), never by refreshing the route.
 */
export function useRealtimeReconnect(callback: () => void): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => realtimeClient.onReconnect(() => callbackRef.current()), []);
}

/**
 * How long a non-open status must persist before consumers are told about it.
 *
 * This hook is read in a dozen places, each holding component state, so every
 * status transition re-renders a dozen subtrees across the app. That is fine
 * for a real disconnect and actively harmful for a blip: the gateway runs on
 * ephemeral instances, so the socket cycles `open → closed → connecting →
 * open` routinely, and propagating each step meant the whole app re-rendered
 * every few seconds. Those re-renders are not free under Next 16 — they churn
 * `<Activity>` boundaries, whose effects are destroyed and re-created on every
 * hide/show, which is how a re-render storm turns into a history-entry leak.
 *
 * Upgrades to `open` propagate immediately; only downgrades wait. A socket
 * that recovers inside the window never reaches React at all.
 */
const STATUS_DOWNGRADE_DEBOUNCE_MS = 3_000;

/** Current connection status, for status pills / debugging. */
export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(
    realtimeClient.currentStatus,
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clearPending = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const apply = (next: RealtimeStatus) => {
      clearPending();
      // `open` is good news and `disabled` is a deliberate state change — both
      // should be visible at once. Everything else is potentially a blip.
      if (next === "open" || next === "disabled") {
        setStatus(next);
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        setStatus(next);
      }, STATUS_DOWNGRADE_DEBOUNCE_MS);
    };

    const unsubscribe = realtimeClient.onStatus(apply);
    // Reconcile the snapshot taken during render: a status may have changed
    // between it and this effect (a socket that opened first and subscribed
    // second reports no event for it), which would otherwise leave the hook —
    // and the fallback-poll intervals built on it — at the stale value.
    // Runs after subscribing so a transition landing in that window is still
    // seen exactly once, in order.
    apply(realtimeClient.currentStatus);

    return () => {
      clearPending();
      unsubscribe();
    };
  }, []);

  return status;
}
